import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { OrdersContext } from './context';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ENUM_ORDERS_TYPE } from 'utils/enum';
import { LocationType } from 'models/cokitchen.model';
import bulkOrderService from 'services/bulk-order.service';
import { SocketIOConnection } from 'utils/socket-client';
import { BrandType } from 'models/brand.model';
import { isBrandOpenNow, cleanObject, handleApiError } from 'utils/functions';
import {
  CalculatedOrderType,
  CreateGroupOrderType,
  CreateOrderObjectType,
  OrderMealType,
  OrderObjectType,
  OrderTypesType
} from 'models/order.model';
import { MealType } from 'models/meal.model';
import { AddonCategoryType } from 'models/addon-category.model';
import { AddressFormatType } from 'models/address.model';
import moment from 'moment';
import { CompanyWalletType } from 'models/wallet.model';
import { CompaniesListType } from 'models/company.model';

const DEFAULT_PHONE_NUMBER = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER) || '';
const COKITCHEN_LEKKI_ID = '85c7d173-56ed-44c8-8335-ef0eecabf454';

export const emptyCalculatedOrderForm = {
  cokitchen_polygon_id: '',
  address_details: {
    city: '',
    name: '',
    address_line: '',
    phone_number: DEFAULT_PHONE_NUMBER,
    building_number: ''
  },
  discount_code: '',
  appreciation_note: '',
  lat: '0',
  lng: '0',
  pickup: false,
  meals: [],
  scheduled: false,
  recurring: false,
  recurrence: '',
  selected_days: [],
  scheduled_delivery_date: '',
  scheduled_delivery_time: '',
  recurring_order_end_date: ''
};

const createEmptyOrderForm = (
  companyWalletDetails: CompanyWalletType | undefined
): CreateOrderObjectType => ({
  order_type_id: '',
  calculated_order_id: '',
  order_details: {
    rider_note: '',
    appreciation_note: '',
    cutlery: true,
    use_bani: false,
    bank_transfer: false,
    use_business_wallet: false,
    use_paystack: false,
    wallets: [
      {
        wallet_amount: String(companyWalletDetails?.available_balance),
        wallet_id: companyWalletDetails?.id ?? ''
      }
    ]
  }
});

const createEmptyGroupOrderObject = (activeCompany: CompaniesListType): CreateGroupOrderType => ({
  address_details: {
    building_number: '',
    city: '',
    address_line: '',
    name: '',
    phone_number: ''
  },
  lat: '',
  lng: '',
  cokitchen_polygon_id: '',
  pickup: false,
  group_split: false,
  group_allow_external_invite: false,
  group_spend_limit: '0',
  company_id: activeCompany?.id ?? '',
  company_user_ids: []
});
const OrdersProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const {
    activeCompany,
    refetchSavedAddressList,
    companyWalletDetails,
    setBasketValue,
    setIsBasketDisabled
  } = useDashboardContext();
  const [calculatedOrderLoading, setCalculatedOrderLoading] = useState(false);
  const [restaurants, setRestaurants] = useState<BrandType[]>([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [restaurant, setRestaurant] = useState<BrandType | null>(null);
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [bulkOrderMeals, setBulkOrderMeals] = useState<OrderMealType[]>([]);
  const [groupOrderMeals, setGroupOrderMeals] = useState<OrderMealType[]>([]);
  const [batchOrderMeals, setBatchOrderMeals] = useState<OrderMealType[]>([]);
  const [coordinates, setCoordinates] = useState({ lat: '', lng: '' });
  const [meal, setMeal] = useState<MealType | null>(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [calculatedOrder, setCalculatedOrder] = useState({} as CalculatedOrderType);
  const [orderType, setOrderType] = useState<OrderTypesType[]>([]);
  const [cokitchenId, setCokitchenId] = useState('');
  const storedCalculatedOrder = localStorage.getItem(STORAGE_KEYS.CALCULATED_ORDER);
  const parsedCalculatedOrder =
    storedCalculatedOrder !== null ? JSON.parse(storedCalculatedOrder) : null;
  const [calculatedOrderForm, setcalculatedOrderForm] = useState<OrderObjectType>(
    parsedCalculatedOrder ?? emptyCalculatedOrderForm
  );

  const [createOrderForm, setCreateOrderForm] = useState(
    createEmptyOrderForm(companyWalletDetails)
  );
  const [createGroupOrderObject, setCreateGroupOrderObject] = useState(
    createEmptyGroupOrderObject(activeCompany)
  );
  const [cokitchenPolygon, setCokitchenPolygon] = useState({} as LocationType);
  const [currentAddress, setCurrentAddress] = useState('');
  const [saveAddressloading, setSaveAddressLoading] = useState(false);
  const [currentAddressLoading, setCurrentAddressLoading] = useState(false);
  const [locationAccess, setLocationAccess] = useState(false);
  const [confirmedQuickeats, setConfirmedQuickeats] = useState(false);
  const firstLoad = useRef(true);
  const { pathname } = useLocation();

  const [deliveryDate, setDeliveryDate] = useState(moment().format('YYYY-MM-DD'));

  const typeOfOrder = useMemo(() => {
    const isGroup = pathname === ROUTE_KEYS.GROUP_BASKET;
    const isBulk = pathname === ROUTE_KEYS.BULK_BASKET;
    const isBatch = pathname === ROUTE_KEYS.BATCH_BASKET;
    if (isGroup) return ENUM_ORDERS_TYPE.GROUP;
    if (isBulk) return ENUM_ORDERS_TYPE.BULK;
    if (isBatch) return ENUM_ORDERS_TYPE.BATCH;
    return (searchParams.get('type') as ENUM_ORDERS_TYPE) ?? '';
  }, [pathname]);

  useEffect(() => {
    if (typeOfOrder === ENUM_ORDERS_TYPE.GROUP && calculatedOrder?.id) {
      connectSocket();
    }
  }, [calculatedOrder?.id, typeOfOrder]);

  const calculatedOrderId = localStorage.getItem(STORAGE_KEYS.CALCULATED_ORDER_ID);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CALCULATED_ORDER, JSON.stringify(calculatedOrderForm));
  }, [calculatedOrderForm]);
  const fetchData = async () => {
    const res = await apiService.get(`orders/get-calculated-order?id=${calculatedOrderId}`, {
      baseURL: process.env.REACT_APP_ORDER_API_URL
    });
    setCalculatedOrder(res?.data?.data);
  };
  useEffect(() => {
    if (calculatedOrderId && !calculatedOrder?.id && typeOfOrder === ENUM_ORDERS_TYPE.GROUP) {
      fetchData();
    }
  }, [calculatedOrder?.id]);

  const connectSocket = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `group-order:${calculatedOrder?.id}`;
    await conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);

    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
      await fetchData();
    });

    conn.addEventListener(roomName, 'group-order-created', (data: any) => {
      setCalculatedOrder(data);
    });
  };
  useEffect(() => {
    if (calculatedOrderForm.lat !== '0' && calculatedOrderForm.lng !== '0') {
      setCoordinates({
        lat: calculatedOrderForm.lat,
        lng: calculatedOrderForm.lng
      });
    } else {
      if (navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocationAccess(true);
            setCurrentAddressLoading(true);
            setCoordinates({
              lat: String(position.coords.latitude),
              lng: String(position.coords.longitude)
            });
          },
          (error) => {
            if (error.PERMISSION_DENIED) {
              setLocationAccess(false);
            }
          }
        );
      }
    }
  }, []);

  const getOrderType = async () => {
    try {
      const res = await apiService.get<{ data: OrderTypesType[] }>(`order/order-types`, {
        baseURL: process.env.REACT_APP_ORDER_API_URL
      });
      setOrderType(res.data?.data);
    } catch (error) {
      handleApiError(error);
    }
  };
  useEffect(() => {
    getOrderType();
  }, []);

  const loadRestaurants = async () => {
    const params = {
      cokitchen_id: COKITCHEN_LEKKI_ID,
      item_type: 'FOOD',
      is_fcb: false
    };
    try {
      setRestaurantsLoading(true);
      const res = await apiService.get<BrandType[]>('brand_management/brands', {
        params,
        baseURL: process.env.REACT_APP_BRAND_API_URL
      });
      const restuarantsArray = res.data.filter((r) => r.live);
      setRestaurants(
        restuarantsArray.sort((a, b) => {
          return isBrandOpenNow(a) === isBrandOpenNow(b) ? 0 : isBrandOpenNow(a) ? -1 : 1;
        })
      );
    } catch (error) {
      handleApiError(error);
    } finally {
      setRestaurantsLoading(false);
    }
  };

  useEffect(() => {
    setcalculatedOrderForm({
      ...calculatedOrderForm,
      meals: bulkOrderMeals?.map((meal) => ({
        id: meal.id,
        quantity: meal.quantity,
        order_note: meal.order_note,
        members: meal.members?.map((m) => ({
          id: m.id,
          first_name: m.firstName,
          last_name: m.lastName
        })),
        addons: meal?.addons?.map((item) => {
          return { id: item.id, quantity: item.quantity };
        })
      }))
    });
  }, [bulkOrderMeals]);

  useEffect(() => {
    setcalculatedOrderForm({
      ...calculatedOrderForm,
      meals: batchOrderMeals?.map((meal) => ({
        id: meal.id,
        quantity: meal.quantity,
        order_note: meal.order_note,
        members: meal.members?.map((m) => ({
          id: m.id,
          first_name: m.firstName,
          last_name: m.lastName
        })),
        addons: meal?.addons?.map((item) => {
          return { id: item.id, quantity: item.quantity };
        })
      }))
    });
  }, [batchOrderMeals]);

  useEffect(() => {
    if (cokitchenId) loadRestaurants();
  }, [cokitchenId]);
  const loadRestaurant = async (id?: string) => {
    if (!id || id === restaurant?.id) return;
    try {
      setRestaurantLoading(true);
      const res = await apiService.get<{ data: BrandType }>(`brand_management/brands/${id}`, {
        baseURL: process.env.REACT_APP_BRAND_API_URL
      });
      const restaurant = res.data?.data;
      restaurant.meal_category = await getMealCategories(id);
      setRestaurant(restaurant);
    } catch (error) {
      handleApiError(error);
    } finally {
      setRestaurantLoading(false);
    }
  };
  const getMealCategories = async (id: string) => {
    const res = await apiService.get(`marketing/brand/meal-categories/${id}`, {
      baseURL: process.env.REACT_APP_BRAND_API_URL
    });
    return Promise.all(
      res.data.data.map(async (category: any) => {
        return {
          id: category.id,
          name: category.name,
          meals: await getMeals(category.id)
        };
      })
    );
  };

  const getMeals = async (id: string) => {
    const res = await apiService.get(`marketing/meal-categories/meals/${id}`, {
      baseURL: process.env.REACT_APP_BRAND_API_URL
    });
    return res?.data?.map((m: any) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      price: Number(m.amount),
      image: m?.images?.[0],
      active: m.active,
      available_no: m.available_no
    })) as MealType[];
  };
  const loadMeal = async (id?: string) => {
    if (!id || id === meal?.id) return;
    try {
      setMealLoading(true);
      const res: any = await apiService.get(`marketing/get-meal-by-id/${id}`, {
        baseURL: process.env.REACT_APP_BRAND_API_URL
      });
      const meal = res.data.data;
      setMeal({
        id: meal.id,
        name: meal.name,
        description: meal.description,
        price: Number(meal.amount),
        image: meal?.images?.[0],
        restaurant: { ...meal.brand, logo: restaurants.find((r) => r.id === meal.brand.id)?.logo },
        addon_categories: await getAddonCategories(meal.id),
        active: meal.active,
        available_no: meal.available_no
      });
    } catch (error) {
      handleApiError(error);
    } finally {
      setMealLoading(false);
    }
  };
  const getCokitchPolygonId = async () => {
    try {
      const res = await apiService.post<LocationType>(`internal/location`, {
        baseURL: process.env.REACT_APP_CORE_API_URL,
        isInternal: true,
        data: {
          lat: coordinates?.lat,
          lng: coordinates?.lng
        }
      });
      setCokitchenId(res.data.cokitchen.id);
      setCokitchenPolygon(res.data);
      setcalculatedOrderForm({
        ...calculatedOrderForm,
        cokitchen_polygon_id: res.data.cokitchen_polygon.id
      });
      setCreateGroupOrderObject((prevState) => ({
        ...prevState,
        cokitchen_polygon_id: res.data.cokitchen_polygon.id
      }));
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    if (coordinates.lat !== '' && coordinates.lng !== '') {
      getCokitchPolygonId();
    }
  }, [coordinates.lat, coordinates.lng, locationAccess]);

  const getAddonCategories = async (meal_id: string) => {
    const data = { meal_id, by_category: true };
    const res = await apiService.post('internal/meal-addons', {
      data,
      isInternal: true,
      baseURL: process.env.REACT_APP_BRAND_API_URL
    });

    return res.data.data.addons.map((category: any) => ({
      id: category.id,
      name: category.base_addon_category.name,
      summary: category.summary,
      selection_type: category.meal_category_selection_type.name,
      max_selection: Number(category.selection_no) || Infinity,
      addons: category.addons.map((addon: any) => ({
        id: addon.id,
        name: addon.name,
        quantity: Number(addon.quantity) || 0,
        is_default: addon.addon_meal_id === category.meal_default_id,
        price: Number(addon.amount)
      }))
    })) as AddonCategoryType[];
  };

  const saveToLocalStorage = () => {
    if (typeOfOrder === ENUM_ORDERS_TYPE.BULK) {
      localStorage.setItem(STORAGE_KEYS.ORDER, JSON.stringify(bulkOrderMeals));
    } else if (typeOfOrder === ENUM_ORDERS_TYPE.BATCH) {
      localStorage.setItem(STORAGE_KEYS.BATCH_ORDER, JSON.stringify(batchOrderMeals));
    } else {
      localStorage.setItem(STORAGE_KEYS.GROUP_ORDER, JSON.stringify(calculatedOrder));
    }
  };
  useEffect(() => {
    if (!firstLoad.current) {
      saveToLocalStorage();
    }
  }, [bulkOrderMeals, groupOrderMeals, batchOrderMeals, calculatedOrder]);
  useEffect(() => {
    if (!firstLoad.current) {
      localStorage.setItem(STORAGE_KEYS.CALCULATED_ORDER_ID, calculatedOrder?.id);
    }
    firstLoad.current = false;
  }, [calculatedOrder?.id]);
  useEffect(() => {
    const subTotal =
      Number(calculatedOrder?.total_amount) -
      Number(calculatedOrder?.delivery_fee) -
      Number(calculatedOrder?.delivery_service_charge) -
      Number(calculatedOrder?.service_charge);
    if (typeOfOrder === ENUM_ORDERS_TYPE.GROUP && subTotal < 0) return;
    setBasketValue(subTotal);
  }, [calculatedOrder]);

  useEffect(() => {
    if (typeOfOrder === ENUM_ORDERS_TYPE.GROUP && calculatedOrder?.meals?.length === 0) {
      setIsBasketDisabled(true);
    } else {
      setIsBasketDisabled(false);
    }
  }, [typeOfOrder, calculatedOrder?.meals]);

  useEffect(() => {
    const bulkPersistedOrder = localStorage.getItem(STORAGE_KEYS.ORDER) || '';
    const batchPersistedOrder = localStorage.getItem(STORAGE_KEYS.BATCH_ORDER) || '';
    const groupPersistedOrder = localStorage.getItem(STORAGE_KEYS.GROUP_ORDER) || '';
    if (bulkPersistedOrder && typeOfOrder === ENUM_ORDERS_TYPE.BULK) {
      const parsePersistedOrder = JSON.parse(bulkPersistedOrder);
      setBulkOrderMeals(parsePersistedOrder);
    }
    if (batchPersistedOrder && typeOfOrder === ENUM_ORDERS_TYPE.BATCH) {
      const parsePersistedOrder = JSON.parse(batchPersistedOrder);
      setBatchOrderMeals(parsePersistedOrder);
    }
    if (groupPersistedOrder !== '' && typeOfOrder === ENUM_ORDERS_TYPE.GROUP) {
      const parsePersistedOrder = JSON.parse(groupPersistedOrder);
      setCalculatedOrder(parsePersistedOrder);
    }
  }, []);
  useEffect(() => {
    if (coordinates.lat !== '' && coordinates.lng !== '' && currentAddress === '') {
      getCurrentAddress();
    }
  }, [coordinates, currentAddress]);

  const getCurrentAddress = async () => {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coordinates.lat},${coordinates.lng}&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`
    );
    const data = await response.json();
    setCurrentAddress(data.results[0].formatted_address);
    setCurrentAddressLoading(false);
  };
  const saveAddresses = async (address: AddressFormatType) => {
    try {
      await apiService.post(`user-saved-address`, {
        baseURL: process.env.REACT_APP_CORE_API_URL,
        data: address
      });
      await refetchSavedAddressList();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleSaveAddress = async (address: string, name: string) => {
    try {
      setSaveAddressLoading(true);
      const formattedAddress = await bulkOrderService.FormatAddress(address, name);
      await saveAddresses(formattedAddress);
      setSaveAddressLoading(false);
    } catch {
      console.log('error');
    }
  };

  const getOrderDetails = async () => {
    if (
      !cokitchenPolygon?.cokitchen_polygon?.id ||
      !calculatedOrderForm.address_details?.address_line ||
      calculatedOrder.lat === '0' ||
      calculatedOrderForm.lng === '0'
    )
      return;
    setCalculatedOrderLoading(true);
    setIsBasketDisabled(true);
    const formatCalculatedOrderForm = cleanObject(calculatedOrderForm);
    if (!formatCalculatedOrderForm.meals) {
      if (typeOfOrder === ENUM_ORDERS_TYPE.BULK) {
        formatCalculatedOrderForm.meals = bulkOrderMeals?.map((meal) => ({
          id: meal.id,
          quantity: meal.quantity
        }));
      } else if (typeOfOrder === ENUM_ORDERS_TYPE.BATCH) {
        formatCalculatedOrderForm.meals = batchOrderMeals?.map((meal) => ({
          id: meal.id,
          quantity: meal.quantity
        }));
      } else {
        formatCalculatedOrderForm.meals = [];
      }
    }
    const data = {
      ...formatCalculatedOrderForm,
      company_id: activeCompany?.id,
      cokitchen_polygon_id: calculatedOrderForm.cokitchen_polygon_id,
      is_bulk_order: typeOfOrder === ENUM_ORDERS_TYPE.BULK,
      company_order: true,
      ...(calculatedOrderForm.preorder ? { preorder: true } : {})
    };

    if (typeOfOrder === ENUM_ORDERS_TYPE.BATCH) {
      delete data.scheduled_delivery_time;
      if (data.preorder) delete data.batch_order_date;
      else {
        delete data.preorder;
        delete data.preorder_delivery_date;
        delete data.preorder_delivery_time;
        data.batch_order_date = deliveryDate;
      }
    }
    try {
      const res = await apiService.post('order/calculate', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data
      });
      setCalculatedOrder(res?.data?.calculated_order);
    } catch (err) {
      handleApiError(err);
    } finally {
      setCalculatedOrderLoading(false);
      setIsBasketDisabled(false);
    }
  };
  useEffect(() => {
    setTimeout(() => getOrderDetails(), 2000);
  }, [calculatedOrderForm.meals]);

  const handleReset = () => {
    setBulkOrderMeals([]);
    setGroupOrderMeals([]);
    setBatchOrderMeals([]);
    setCalculatedOrder({} as CalculatedOrderType);
    setcalculatedOrderForm({
      ...emptyCalculatedOrderForm,
      cokitchen_polygon_id: calculatedOrderForm.cokitchen_polygon_id || ''
    });
    setCreateOrderForm(createEmptyOrderForm(companyWalletDetails));
    setCreateGroupOrderObject({
      ...createEmptyGroupOrderObject(activeCompany),
      cokitchen_polygon_id: createGroupOrderObject.cokitchen_polygon_id
    });
    setBasketValue(0);
    setConfirmedQuickeats(false);
    localStorage.setItem(STORAGE_KEYS.CALCULATED_ORDER_ID, '');
    localStorage.setItem(STORAGE_KEYS.GROUP_ORDER, '{}');
    localStorage.setItem(STORAGE_KEYS.BATCH_ORDER, '[]');
    localStorage.setItem(STORAGE_KEYS.ORDER, '[]');
  };

  return (
    <OrdersContext.Provider
      value={{
        restaurants,
        restaurantsLoading,
        loadRestaurants,
        restaurant,
        restaurantLoading,
        loadRestaurant,
        bulkOrderMeals,
        setBulkOrderMeals,
        batchOrderMeals,
        setBatchOrderMeals,
        groupOrderMeals,
        setGroupOrderMeals,
        meal,
        mealLoading,
        loadMeal,
        createGroupOrderObject,
        setCreateGroupOrderObject,
        calculatedOrderForm,
        setcalculatedOrderForm,
        calculatedOrder,
        setCalculatedOrder,
        createOrderForm,
        setCreateOrderForm,
        setCokitchenPolygon,
        cokitchenPolygon,
        orderType,
        setOrderType,
        currentAddress,
        saveAddressloading,
        currentAddressLoading,
        handleSaveAddress,
        setCoordinates,
        locationAccess,
        calculatedOrderLoading,
        setCalculatedOrderLoading,
        handleReset,
        deliveryDate,
        setDeliveryDate,
        confirmedQuickeats,
        setConfirmedQuickeats
      }}>
      {children}
    </OrdersContext.Provider>
  );
};

export default OrdersProvider;
